/**
 * PORT (wide screens): the battle log of the wide screen's HUD (ui/wide-screen.ts, the group `log`: the game's
 * status_text at the top left, enlarged) over the battlefield. The original's log lay on its own dark sky in a corner
 * of an 807x600 window; enlarged on a phone it covers a good part of the map (5 lines, ~140 CSS px), and its yellow
 * text is lost over light terrain and trees. So, while the HUD is laid out:
 *  - a soft dark backdrop behind the lines shown (their ink, not the field's box);
 *  - folded: a few seconds after the log last changed (a turn starts, a worm is hit) it shows only its newest lines of
 *    the running turn (after the last turn separator, the game's line of dashes: proc.add_status), at most `lines`
 *    of them; a new line unfolds it again for those seconds. The HUD laid out anew (a resize, a step of the host's
 *    dynamic resolution when frames are slow, a group shown or hidden) is no new line: a folded log stays folded, an
 *    unfolded one folds when it would have.
 * Presentation only: the text is the game's, only the field's height and scroll change (the wide screen's `wrap`
 * narrows its lines already). The shown lines' rectangle is where the battle camera keeps the current worm clear of the
 * log (FWP.camera_keep_top).
 */
import { Sprite } from '@flash';

/** A rectangle in stage coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** How the log folds and its backdrop. */
export interface LogFoldSpec {
  /** Lines of the running turn shown folded (the newest). */
  lines: number;
  /** How long (s) the log stays unfolded after its text changed. */
  seconds: number;
  /** The backdrop's colour and opacity. */
  backdrop: { color: number; alpha: number };
}

/** The field's gutter (px as the field draws it: Flash's 2 px). */
const GUTTER = 2;
/** The backdrop's room around the text (px as the field draws it) and its corners' radius. */
const PAD = 3;
const RADIUS = 8;

/** A line of the log made only of this character, two or more: the game's turn separator. */
function isRule(line: string, ch: string): boolean {
  return line.length >= 2 && line === ch.repeat(line.length);
}

export class LogFold {
  private readonly backdrop: Sprite;
  /** The field's height as the HUD laid it out (unfolded). */
  private full = 0;
  private folded = false;
  /** The field's height folded. */
  private foldedH = -1;
  /** The text the log's clock runs from (null: not laid out yet). */
  private text: string | null = null;
  private since = 0;
  private drawn = '';

  /**
   * @param field the log's text field (a child of `group`)
   * @param group the HUD group's sprite (the backdrop goes under its children)
   * @param rule the character of the turn separator's line
   * @param now the clock (ms)
   */
  constructor(
    private readonly field: any,
    private readonly group: any,
    private readonly spec: LogFoldSpec,
    private readonly rule: string,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.backdrop = new Sprite();
    this.backdrop.name = '$wide_log_backdrop';
    this.backdrop.mouseEnabled = false;
    this.backdrop.mouseChildren = false;
    group.addChildAt(this.backdrop, 0);
  }

  /** The HUD laid the log out anew (its field's height as laid out: unfolded); `on`: the wide HUD is on. */
  layout(on: boolean): void {
    const t = String(this.field.text);
    const same = on && this.text !== null && this.sameLines(t, this.text);
    const refold = same && this.folded;
    // (the height the HUD gave it: folded still where the HUD left the field's height alone)
    if (this.folded && this.field.height === this.foldedH) this.field.height = this.full;
    if (this.folded && !on) this.field.scrollV = 1;
    this.full = this.field.height;
    this.folded = false;
    this.text = t;
    // The same lines laid out anew keep the log's clock and its fold.
    if (!same) this.since = this.now();
    if (refold) this.fold();
    this.backdrop.visible = on;
    this.drawn = '';
    if (on) this.draw();
    else this.backdrop.graphics.clear();
  }

  /** Every frame while the wide HUD is on: folds or unfolds the log, and follows its lines with the backdrop. */
  frame(): void {
    const f = this.field;
    const t = String(f.text);
    const now = this.now();
    if (t !== this.text) {
      const lines = this.text !== null && this.sameLines(t, this.text);
      this.text = t;
      if (!lines) {
        this.since = now;
        if (this.folded) this.unfold();
      }
    } else if (!this.folded && now - this.since >= this.spec.seconds * 1000) this.fold();
    this.draw();
  }

  /** Whether it shows only the running turn's newest lines now. */
  get isFolded(): boolean {
    return this.folded;
  }

  /** The lines shown (their ink with the backdrop's room) in stage coordinates, or null: none. */
  shownRect(): Area | null {
    const r = this.inkRect();
    if (!r || !this.group.stage) return null;
    const b = this.backdrop.getBounds(this.group.stage);
    return b.width > 0 && b.height > 0 ? { x: b.x, y: b.y, width: b.width, height: b.height } : null;
  }

  /** Whether two texts of the field are the same lines (a turn separator fitted anew to the field's width is the same). */
  private sameLines(a: string, b: string): boolean {
    if (a === b) return true;
    const key = (t: string) => t.split(/\r\n|\r|\n/).map((l) => (isRule(l, this.rule) ? this.rule + this.rule : l)).join('\n');
    return key(a) === key(b);
  }

  private unfold(): void {
    const f = this.field;
    this.folded = false;
    if (f.height !== this.full) f.height = this.full;
    f.scrollV = f.maxScrollV;
  }

  /** The running turn's newest lines only (after the last separator), at most `spec.lines` of them. */
  private fold(): void {
    const f = this.field;
    const n = Number(f.numLines) || 0;
    if (n <= 0) return;
    const text = (i: number) => String(f.getLineText(i)).replace(/[\r\n]+$/, '');
    let last = n - 1;
    while (last > 0 && text(last) === '') last--;
    let first = last;
    while (first > 0 && last - first + 1 < this.spec.lines && text(first - 1) !== '' && !isRule(text(first - 1), this.rule)) first--;
    const shown = last - first + 1; // (scrolled to its end: the field's text ends with a line break)
    const lineH = f.getLineMetrics(Math.min(last, n - 1)).height;
    const h = shown * lineH + 2 * GUTTER + 1;
    this.folded = true;
    if (h < this.full) f.height = h;
    this.foldedH = f.height;
    f.scrollV = f.maxScrollV;
  }

  /** The shown lines' ink in the group's coordinates (the field's, placed), or null. */
  private inkRect(): Area | null {
    const f = this.field;
    const n = Number(f.numLines) || 0;
    const top = Math.max(1, Number(f.scrollV) || 1);
    const bottom = Math.min(n, Number(f.bottomScrollV) || n);
    let left = Infinity;
    let right = 0;
    let height = 0;
    let acc = 0;
    for (let i = top - 1; i < bottom; i++) {
      const m = f.getLineMetrics(i);
      acc += m.height;
      if (String(f.getLineText(i)).replace(/[\r\n]+$/, '') === '' || !(m.width > 0)) continue;
      height = acc; // (down to the last line with text)
      left = Math.min(left, m.x);
      right = Math.max(right, m.x + m.width);
    }
    if (!(right > left) || !(height > 0)) return null;
    const sx = f.scaleX || 1;
    const sy = f.scaleY || 1;
    return { x: f.x + (left - PAD) * sx, y: f.y + (GUTTER - PAD) * sy, width: (right - left + 2 * PAD) * sx, height: (height + 2 * PAD) * sy };
  }

  private draw(): void {
    const r = this.inkRect();
    const key = r ? `${r.x.toFixed(1)},${r.y.toFixed(1)},${r.width.toFixed(1)},${r.height.toFixed(1)}` : '';
    if (key === this.drawn) return;
    this.drawn = key;
    const g = this.backdrop.graphics;
    g.clear();
    if (!r) return;
    g.beginFill(this.spec.backdrop.color, this.spec.backdrop.alpha);
    g.drawRoundRect(r.x, r.y, r.width, r.height, RADIUS, RADIUS);
    g.endFill();
  }
}
